import PopupBasic from 'src/game/components/popups/PopupBasic';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { Card, CardState } from '../components/Card';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import {
  cardsOpenRequestProposeList,
  cardsProposeTradeToCandidate,
} from '../cards.sequence';
import { cardsShareDonate, cardsShareRequest } from '../cards.share';
import { CardId, tradeLimits } from 'src/replicant/cards/cards.ruleset';
import { getScreenDimensions } from 'src/lib/utils';
import i18n from 'src/lib/i18n/i18n';
import {
  cardsCanReceiveCard,
  cardsCanSendCard,
  cardsCanStartTrade,
  cardsGetTradeReceivedForLimit,
  cardsGetTradesStartedForLimit,
  cardsGetTradeSentForLimit,
} from 'src/replicant/cards/cards.getters';
import StateObserver from 'src/StateObserver';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import CardsDailyLimitTimer from '../components/CardsDailyLimitTimer';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';

import { AssetGroup } from '@play-co/timestep-core';

const CARD_SCALE = 3;
const BUTTON_WIDTH = 420;
const BUTTON_HEIGHT = 100;
const REQUEST_BUTTON_WIDTH = 497;
const REQUEST_BUTTON_HEIGHT = 100;
const MESSAGE_HEIGHT = 160;
const MESSAGE_Y = 1030;
const PRIMARY_BUTTON_Y = 1025;
const SECONDARY_BUTTON_Y = 1140;
const DAILY_LIMIT_TEXT_Y = 1240;
const ACTION_ICON_SIZE = 56;
const BASE_LAYOUT_HEIGHT = 1280;

type PopupState = 'send' | 'request' | 'none';

export default class PopupCardDetails extends PopupBasic<
  (sent: number) => void
> {
  private card: Card;
  private cardId!: CardId;
  private ctaRequestLine: ButtonScaleViewWithText;
  private ctaRequestOthers: ButtonScaleViewWithText;
  private ctaShare: ButtonScaleViewWithText;
  private ctaTrade: ButtonScaleViewWithText;
  private currentState!: CardState;
  private sent: number = 0;
  private contentOffsetY: number;
  private limitTimer: CardsDailyLimitTimer;
  private dailyLimitText: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: (sent: number) => void }) {
    const width = opts.superview.style.width;
    const height = getScreenDimensions().height;
    const contentOffsetY = Math.max(0, (height - BASE_LAYOUT_HEIGHT) / 2);

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => {
        opts.close(this.sent);
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.DETAILS,
      },
      zIndex: 10000,
    });

    this.contentOffsetY = contentOffsetY;

    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.92)',
    });

    this.buttonClose.updateOpts({
      x: this.box.style.width - 55,
      y: 65,
      zIndex: 1,
    });

    this.card = new Card({
      superview: this.box,
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 550 + contentOffsetY,
      scale: CARD_SCALE,
    });

    this.message.updateOpts({
      width: this.box.style.width - 100,
      height: MESSAGE_HEIGHT,
      x: this.box.style.width / 2,
      y: MESSAGE_Y + contentOffsetY,
      font: 'Body',
      size: 30,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      color: '#FFFFFF',
      visible: false,
    });

    this.ctaRequestLine = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      width: REQUEST_BUTTON_WIDTH,
      height: REQUEST_BUTTON_HEIGHT,
      x: this.box.style.width / 2,
      y: PRIMARY_BUTTON_Y + contentOffsetY,
      centerOnOrigin: true,
      fontSize: 36,
      font: 'Body',
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.REQUEST,
      },
      localeText: () => i18n('cards.detailsPopup.requestLine'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.requestCard();
      },
      visible: false,
    });

    this.ctaRequestOthers = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.blue,
      width: REQUEST_BUTTON_WIDTH,
      height: REQUEST_BUTTON_HEIGHT,
      x: this.box.style.width / 2,
      y: SECONDARY_BUTTON_Y + contentOffsetY,
      centerOnOrigin: true,
      fontSize: 36,
      font: 'Body',
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.REQUEST,
      },
      localeText: () => i18n('cards.detailsPopup.requestOthers'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.requestCardFromOthers();
      },
      visible: false,
    });

    this.ctaShare = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      width: BUTTON_WIDTH,
      height: BUTTON_HEIGHT,
      x: this.box.style.width / 2,
      y: PRIMARY_BUTTON_Y + contentOffsetY,
      centerOnOrigin: true,
      fontSize: 42,
      font: 'Body',
      labelOffsetX: 34,
      labelWidth: BUTTON_WIDTH - 34,
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.SEND,
      },
      localeText: () => i18n('cards.detailsPopup.share'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.sendCard();
      },
      visible: false,
    });

    new ImageScaleView({
      superview: this.ctaShare,
      image: 'assets/ui/friends/popupAddFriend/share_icon.png',
      width: ACTION_ICON_SIZE,
      height: ACTION_ICON_SIZE,
      scaleMethod: 'contain',
      centerOnOrigin: true,
      x: 112,
      y: BUTTON_HEIGHT / 2,
      canHandleEvents: false,
    });

    this.ctaTrade = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.blue,
      width: BUTTON_WIDTH,
      height: BUTTON_HEIGHT,
      x: this.box.style.width / 2,
      y: SECONDARY_BUTTON_Y + contentOffsetY,
      centerOnOrigin: true,
      fontSize: 42,
      font: 'Body',
      labelOffsetX: 34,
      labelWidth: BUTTON_WIDTH - 34,
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.SEND,
      },
      localeText: () => i18n('cards.detailsPopup.trade'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.proposeTrade();
      },
      visible: false,
    });

    new ImageScaleView({
      superview: this.ctaTrade,
      image: 'assets/ui/cards/common/icon_trade.png',
      width: ACTION_ICON_SIZE,
      height: ACTION_ICON_SIZE,
      scaleMethod: 'contain',
      centerOnOrigin: true,
      x: 112,
      y: BUTTON_HEIGHT / 2,
      canHandleEvents: false,
    });

    this.limitTimer = new CardsDailyLimitTimer({
      superview: this.box,
      x: this.box.style.width / 2,
      y: SECONDARY_BUTTON_Y + contentOffsetY,
    });

    this.dailyLimitText = new LangBitmapFontTextView({
      superview: this.box,
      width: this.box.style.width - 100,
      height: 40,
      x: this.box.style.width / 2,
      y: DAILY_LIMIT_TEXT_Y + contentOffsetY,
      centerOnOrigin: true,
      font: 'Body',
      size: 26,
      align: 'center',
      verticalAlign: 'center',
      color: '#FFFFFF',
      localeText: () => '',
      visible: false,
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup(
      AssetGroup.constructURLs([
        'assets/ui/cards/common',
        'assets/ui/friends/popupAddFriend',
      ]),
    );
  }

  public init(cardState: CardState) {
    super.init({});

    this.setState(cardState);
  }

  private setState(cardState: CardState) {
    this.currentState = cardState;
    this.card.setState(cardState);
    this.cardId = cardState.id;

    const user = StateObserver.getState().user;
    const now = StateObserver.now();
    const canShare =
      cardsCanSendCard(user, now) && cardsCanReceiveCard(user, now);
    const canTrade = cardsCanStartTrade(user, now);
    const popupState =
      cardState.count >= 2
        ? 'send'
        : cardState.count === 0
          ? 'request'
          : 'none';
    this.updateDailyLimitText(popupState);

    switch (popupState) {
      case 'send':
        if (canShare || canTrade) {
          this.setSendState(canShare, canTrade);
        } else if (!cardsCanSendCard(user, now)) {
          this.setSendLimitState('send');
        } else if (!cardsCanReceiveCard(user, now)) {
          this.setSendLimitState('receive');
        } else {
          this.setSendLimitState('trade');
        }
        break;
      case 'request':
        if (cardsCanReceiveCard(user, now)) {
          this.setRequestState();
        } else {
          this.setRequestLimitState();
        }
        break;
      case 'none':
        this.setNoneState();
        break;
    }
  }

  private async requestCard() {
    await cardsShareRequest({
      cardId: this.cardId,
      preferredMode: 'line',
    });
  }

  private async requestCardFromOthers() {
    await cardsOpenRequestProposeList(this.cardId);
  }

  private async sendCard() {
    await cardsShareDonate({
      cardId: this.cardId,
      preferredMode: 'line',
    });
  }

  private async proposeTrade() {
    const result = await cardsProposeTradeToCandidate(this.cardId);
    if (!result) return;

    this.sent++;
    this.setState({
      ...this.currentState,
      count: this.currentState.count - 1,
      new: false,
    });
  }

  private setSendState(canShare: boolean, canTrade: boolean) {
    this.limitTimer.hide();
    this.ctaRequestLine.hide();
    this.ctaRequestOthers.hide();
    this.message.hide();

    this.ctaShare.updateOpts({
      visible: canShare,
      y: PRIMARY_BUTTON_Y + this.contentOffsetY,
    });
    this.ctaTrade.updateOpts({
      visible: canTrade,
      y:
        (canShare ? SECONDARY_BUTTON_Y : PRIMARY_BUTTON_Y) +
        this.contentOffsetY,
    });
  }

  private setSendLimitState(limitKind: 'send' | 'receive' | 'trade') {
    this.ctaRequestLine.hide();
    this.ctaRequestOthers.hide();
    this.ctaShare.hide();
    this.ctaTrade.hide();
    this.message.show();
    const limitMessageKeys = {
      send: 'cards.detailsPopup.sendLimitMessage',
      receive: 'cards.detailsPopup.receiveLimitMessage',
      trade: 'cards.detailsPopup.tradeLimitMessage',
    } as const;
    this.message.localeText = () => i18n(limitMessageKeys[limitKind]);

    this.limitTimer.start(limitKind, () => this.setState(this.currentState));
  }

  private setRequestState() {
    this.limitTimer.hide();
    this.ctaShare.hide();
    this.ctaTrade.hide();
    this.ctaRequestLine.show();
    this.ctaRequestOthers.show();
    this.message.hide();
  }

  private setRequestLimitState() {
    this.ctaRequestLine.hide();
    this.ctaRequestOthers.hide();
    this.ctaShare.hide();
    this.ctaTrade.hide();
    this.message.show();
    this.message.localeText = () =>
      i18n('cards.detailsPopup.receiveLimitMessage');
    this.limitTimer.start('receive', () => this.setState(this.currentState));
  }

  private setNoneState() {
    this.ctaRequestLine.hide();
    this.ctaRequestOthers.hide();
    this.ctaShare.hide();
    this.ctaTrade.hide();
    this.limitTimer.hide();
    this.message.show();
    this.message.localeText = () =>
      i18n('cards.detailsPopup.collectMoreMessage');
  }

  private updateDailyLimitText(popupState: PopupState) {
    if (popupState === 'send') {
      this.dailyLimitText.localeText = () => {
        const sendLimit = i18n(
          'cards.detailsPopup.sendDailyLimit',
          this.getCardsSentLimitInfo(),
        );
        const tradeLimit = i18n(
          'cards.detailsPopup.tradeDailyLimit',
          this.getTradeLimitInfo(),
        );
        return `${sendLimit}  ${tradeLimit}`;
      };
      this.dailyLimitText.show();
      return;
    }

    if (popupState === 'request') {
      this.dailyLimitText.localeText = () =>
        i18n(
          'cards.detailsPopup.receiveDailyLimit',
          this.getCardsReceivedLimitInfo(),
        );
      this.dailyLimitText.show();
      return;
    }

    this.dailyLimitText.hide();
  }

  private getCardsSentLimitInfo() {
    const max = tradeLimits.send.amount;
    const sent = cardsGetTradeSentForLimit(
      StateObserver.getState().user,
      StateObserver.now(),
    ).length;
    const current = Math.min(sent, max);
    return { current, max };
  }

  private getCardsReceivedLimitInfo() {
    const max = tradeLimits.receive.amount;
    const received = cardsGetTradeReceivedForLimit(
      StateObserver.getState().user,
      StateObserver.now(),
    ).length;
    const current = Math.min(received, max);
    return { current, max };
  }

  private getTradeLimitInfo() {
    const max = tradeLimits.trade.amount;
    const tradesStarted = cardsGetTradesStartedForLimit(
      StateObserver.getState().user,
      StateObserver.now(),
    ).length;
    const current = Math.min(tradesStarted, max);
    return { current, max };
  }
}
